import {useEffect} from 'react';

import {useGetActivitiesForWidget} from '../../../core-entity/widgets/activities-widget/graphql/hooks/use-get-activities-for-widget';
import {useGetPeepEngagementStats} from '../../../entity-details/graphql/hooks/use-get-peep-engagement-stats';
import {useGetMcfxViqContacts} from '../../../people-iq/graphql/hooks/use-get-mcfx-viq-contacts';

import {useGetPeepBusinessCardInfo} from '../../shared/business-card-widget/graphql/hooks/use-get-peep-business-card-info';
import {useGetAccountChildLeads} from '../../shared/leads-widget/graphql/hooks/use-get-account-child-leads';

import {useGetAccountEmailSequenceData} from '../content/email-sequences-widget/graphql/hooks/use-get-account-email-sequence-data';
import {useGetRelatedContacts} from '../content/related-contacts-widget/graphql/hooks/use-get-related-contacts';
import {useGetAccount} from '../graphql/hooks/use-get-account';
import {useGetAccountLocationData} from '../graphql/hooks/use-get-account-location-data';

// An event listener hook that refetches precise parts of an account based on which
// part of the account was updated.
// Use accountRefetchForEvent to fire an event for this hook, because that way we
// can properly type the events that are being fired to only ones that this hook
// will listen for.
export const useAccountUpdateRefetch = (accountId: string) => {
    const {refetch: refetchAccountLocationData} = useGetAccountLocationData(accountId, true);
    const {refetch: refetchEmailEngagementStats} = useGetPeepEngagementStats(accountId, true);
    const {refetch: refetchEmailSequences} = useGetAccountEmailSequenceData(accountId, true);
    const {refetch: refetchLoggedActivities} = useGetActivitiesForWidget(
        accountId,
        1,
        undefined,
        true
    );
    const {refetch: refetchCurrentRelatedContacts} = useGetRelatedContacts(
        accountId,
        false,
        true,
        null,
        null
    );
    const {refetch: refetchMcfxViqContacts} = useGetMcfxViqContacts(accountId, true);
    const {refetch: refetchBusinessCardInfo} = useGetPeepBusinessCardInfo(accountId, true);
    const {refetch: refetchAccount} = useGetAccount(accountId, true);
    const {refetch: refetchChildLeads} = useGetAccountChildLeads(accountId, true);

    useEffect(() => {
        const refetch: EventListener = (event: Event) => {
            const customEvent = event as CustomEvent<{type: AccountUpdateEvent}>;

            switch (customEvent.detail.type) {
                case 'accountContactsUpdated':
                    refetchCurrentRelatedContacts();
                    refetchMcfxViqContacts();
                // Intentionally fall through; account contacts changes may affect sequence visibility.
                case 'businessCardUpdated':
                case 'dripSequenceCancel':
                case 'manuallyStartedPersonalEmailSequenceCancel':
                case 'personalEmailSequenceCancel':
                case 'personalEmailSequenceStart':
                    refetchEmailSequences();
                    break;
                case 'emailSent':
                    // Small timeout to ensure the email is sent before we
                    // refetch the engagement stats and email sequences
                    setTimeout(() => {
                        refetchEmailEngagementStats();
                        refetchEmailSequences();
                    }, 500);
                    break;
                case 'personalEmailSequenceSendNow':
                    setTimeout(() => {
                        refetchEmailSequences();
                        refetchEmailEngagementStats();
                    }, 1000);
                    Timeline.refreshTimeline();
                    break;
                case 'locationUpdated':
                    refetchAccountLocationData();
                    break;
                case 'activityLogged':
                    refetchLoggedActivities();
                    break;
                case 'accountEnrichmentUpdated':
                    refetchAccount();
                    refetchCurrentRelatedContacts();
                    refetchMcfxViqContacts();
                    refetchBusinessCardInfo();
                    refetchAccountLocationData();
                    break;
                case 'accountHierarchyUpdated':
                    refetchChildLeads();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('accountUpdated', refetch);

        return () => {
            window.removeEventListener('accountUpdated', refetch);
        };
    }, [
        refetchAccountLocationData,
        refetchEmailEngagementStats,
        refetchEmailSequences,
        refetchLoggedActivities,
        refetchCurrentRelatedContacts,
        refetchMcfxViqContacts,
        refetchBusinessCardInfo,
        refetchAccount,
        refetchChildLeads,
    ]);
};

export const accountRefetchForEvent = (event: AccountUpdateEvent) => {
    window.dispatchEvent(new CustomEvent('accountUpdated', {detail: {type: event}}));
};

export type AccountUpdateEvent =
    | 'accountContactsUpdated'
    | 'accountEnrichmentUpdated'
    | 'businessCardUpdated'
    | 'dripSequenceCancel'
    | 'emailSent'
    | 'locationUpdated'
    | 'manuallyStartedPersonalEmailSequenceCancel'
    | 'personalEmailSequenceCancel'
    | 'personalEmailSequenceSendNow'
    | 'personalEmailSequenceStart'
    | 'activityLogged'
    | 'accountHierarchyUpdated';
